import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { inr, formatApiErrorDetail } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Calendar, Users, MessageCircle, Tag, CheckCircle2, XCircle } from "lucide-react";

export default function MyInquiries() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inquiries/me");
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const accept = async (offerId) => {
    try {
      const { data } = await api.post(`/offers/${offerId}/accept`);
      navigate(`/payment/${data.booking.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const decline = async (offerId) => {
    try {
      await api.post(`/offers/${offerId}/decline`);
      toast.success("Offer declined");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <h1 className="text-3xl font-display font-bold mb-2">My inquiries</h1>
      <p className="text-[#717171] mb-8">Custom price requests sent to hosts</p>
      {loading ? <p className="text-[#717171]">Loading...</p> : items.length === 0 ? (
        <div className="text-center py-16 text-[#717171]">
          No inquiries yet. Browse stays and click <strong>Request custom price</strong>.
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((q) => (
            <div key={q.id} data-testid={`inquiry-card-${q.id}`} className="bg-white border border-[#EBEBEB] rounded-2xl p-6">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <h3 className="font-display font-semibold text-lg">{q.hotel_name}</h3>
                <Badge className={
                  q.status === "offered" ? "bg-[#FFB400]/15 text-[#A07000] border-0" :
                  q.status === "accepted" ? "bg-[#008A05]/10 text-[#008A05] border-0" :
                  q.status === "declined" ? "bg-[#C13515]/10 text-[#C13515] border-0" :
                  "bg-[#F0F0F4] text-[#555] border-0"
                }>{q.status}</Badge>
              </div>
              <div className="text-sm text-[#444] flex flex-wrap gap-4 mb-3">
                <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {q.check_in} → {q.check_out} · {q.nights}n</span>
                <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {q.guests} guests</span>
                {q.room_name && <span>· {q.room_name}</span>}
              </div>
              {q.message && (
                <div className="bg-[#F7F7F9] rounded-xl p-3 text-sm text-[#444] mb-3 flex gap-2">
                  <MessageCircle className="w-4 h-4 text-[#717171] mt-0.5" /> {q.message}
                </div>
              )}
              {q.offer && (
                <div className="border-t border-[#EBEBEB] pt-4 mt-2">
                  <p className="text-xs uppercase tracking-wide text-[#717171] mb-2 flex items-center gap-1"><Tag className="w-3 h-3" /> Host offer</p>
                  <div className="flex flex-wrap items-end gap-4 justify-between">
                    <div>
                      <p className="font-display font-bold text-2xl">{inr(q.offer.total_price)} <span className="text-sm font-normal text-[#717171]">total</span></p>
                      <p className="text-xs text-[#717171]">{inr(q.offer.custom_price_per_night)} × {q.offer.nights} nights{q.offer.gst_amount ? ` + ${inr(q.offer.gst_amount)} GST` : ""} + {inr(q.offer.convenience_fee || q.offer.platform_fee)} convenience fee</p>
                      {q.offer.message && <p className="text-sm mt-2 text-[#444]">"{q.offer.message}"</p>}
                    </div>
                    {q.offer.status === "active" && (
                      <div className="flex gap-2">
                        <Button data-testid={`decline-offer-${q.offer.id}`} onClick={() => decline(q.offer.id)} variant="outline" className="rounded-xl">
                          <XCircle className="w-4 h-4 mr-1" />Decline
                        </Button>
                        <Button data-testid={`accept-offer-${q.offer.id}`} onClick={() => accept(q.offer.id)} className="rounded-xl bg-[#FF385C] hover:bg-[#D90B38] text-white">
                          <CheckCircle2 className="w-4 h-4 mr-1" />Accept & pay
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
